import { useQuery } from "@apollo/client";
import { useWorker } from "@koale/useworker";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { AccountSearchContext } from "../../../Context/AccountSearchContext/AccountSearchContext";
import getCurrentUserInformation from "../../../GraphQL/Query/getCurrentUserInformation";
import { Icon } from "../../Icon/Icon";
import { Button } from "../../SharedComponents/Button/Button";
import { SearchBox } from "../../SharedComponents/SearchBox/SearchBox";
import { AdminAccountsList } from "../AdminAccountsList/AdminAccountsList";
import { AdminPageHeader } from "../AdminPageHeader/AdminPageHeader";
import { AdminRightActionsContainer } from "../AdminRightActionsContainer/AdminRightActionsContainer";
import "./AdminAccounts.css";

export const AdminAccounts = () => {
  const { t } = useTranslation();
  const id = Cookies.get("primary_account");
  const { data: currentUserData } = useQuery(getCurrentUserInformation);
  const [filterWorker, { kill }] = useWorker(filterAccounts);
  const history = useHistory();
  const { searchTerm, setSearchTerm } = useContext(AccountSearchContext);
  const [filteredAccounts, setFilteredAccounts] = useState([]);

  const { data, loading } = useQuery(getAllAccounts, {
    fetchPolicy: "network-only",
  });
  const { data: distributorsData } = useQuery(getDistributors, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (data?.accounts && distributorsData?.distributors) {
      if (searchTerm === "") {
        setFilteredAccounts(data?.accounts || []);
      } else {
        kill();
        const accounts = data?.accounts || [];
        filterWorker(accounts, searchTerm, distributorsData?.distributors || [])
          .then((data) => setFilteredAccounts(data))
          .catch((err) => console.error(err));
      }
    } else {
      setFilteredAccounts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm, filterWorker, distributorsData]);

  useEffect(() => {
    return () => {
      setSearchTerm("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="AdminAccounts">
      <div className="AdminDistributors__content">
        <AdminPageHeader title={t("generic.accounts")} />
        <AdminAccountsList
          items={filteredAccounts}
          title={[t("generic.name"), t("generic.status"), t("generic.users")]}
          buttonText={t("accounts.addAccount")}
          type="ACCOUNT"
          listActions={accountActions}
          loading={loading}
        />
      </div>
      <div className="AdminDistributors__right">
        <div className="AdminDistributors__search">
          <SearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
        <div className="AdminDistributors__actions">
          <AdminRightActionsContainer>
            <div className="AdminDistributors__add-new">
              {currentUserData?.user?.roles?.sso?.find(
                (x) => x.accountId === id
              )?.role !== "SUPPORTER" && (
                <Button
                  buttonStyle="Button__primary"
                  click={() => history.push("/admin/accounts/new")}
                >
                  <Icon
                    className="AdminRightActionsContainer__add-new--icon"
                    name="iconAdd"
                    width={12}
                    height={12}
                  />
                  <div className="AdminRightActionsContainer__add-new--text">
                    {t("generic.addNew")}
                  </div>
                </Button>
              )}
            </div>
          </AdminRightActionsContainer>
        </div>
      </div>
    </div>
  );
};

const accountActions = [
  {
    action: "edit",
    label: "settings.edit",
    link: "/edit",
  },
  {
    action: "add-user",
    label: "settings.addUser",
    link: "invite-user",
  },
];

const getAllAccounts = gql`
  query getAllAccounts {
    accounts {
      id
      name
      email
      parentId
      role
      status
      usersCount
    }
  }
`;

const getDistributors = gql`
  query {
    distributors {
      id
      name
      status
      usersCount
    }
  }
`;

const filterAccounts = (accounts, searchTerm, distributors) => {
  if (searchTerm.includes("dist:") && distributors) {
    const split = searchTerm.split(":");
    const distributor = distributors.find((x) => x.name === split[1].trim());
    return accounts.filter((x) => x.parentId === distributor?.id);
  } else {
    return accounts.filter((x) =>
      x.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
};
